import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import { getRadius, getTextSize } from '../../utilities';
import type { TextSize } from '../../variables';

const SkeletonAnimation = css`
  @media (prefers-reduced-motion: no-preference) {
    animation: shimmer 2s infinite linear;
    background: linear-gradient(
      to right,
      var(--lns-color-disabledBackground) 4%,
      var(--lns-color-backgroundHover) 25%,
      var(--lns-color-disabledBackground) 36%
    );
    background-size: 1000px 100%;
    @keyframes shimmer {
      0% {
        background-position: -1000px 0;
      }
      100% {
        background-position: 1000px 0;
      }
    }
  }
`;

const SkeletonTextWrapper = styled.div<SkeletonTextProps>`
  ${props => getTextSize(props.size)};
  color: transparent;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    background-color: var(--lns-color-disabledBackground);
    border-radius: var(--lns-radius-full);
    width: 100%;
    display: block;
    height: 71.45%;
    top: 0.2em;
    ${props => props.animated && SkeletonAnimation}
  }

  ${props =>
    props.lines > 1 &&
    `
      &:nth-of-type(3n+1) {
        width: calc(100% - 2.25rem);
      }
      &:nth-of-type(3n) {
        width: calc(100% - 4.125rem);
      }
    }
  `};
`;

const SkeletonContainerWrapper = styled.div<SkeletonContainerProps>`
  background-color: var(--lns-color-disabledBackground);
  ${props => getRadius(props.radius)};
  height: ${props => props.height};
  width: ${props => props.width};
  ${props => props.animated && SkeletonAnimation}
`;

export const SkeletonText = ({
  size = 'body-md',
  lines = 1,
  animated = false,
}: SkeletonTextProps & React.ComponentProps<typeof SkeletonTextWrapper>) => {
  return (
    <>
      {[...Array(lines)].map((_, i) => (
        <SkeletonTextWrapper
          key={i}
          size={size}
          lines={lines}
          animated={animated}
        >
          Loading
        </SkeletonTextWrapper>
      ))}
    </>
  );
};

export const SkeletonContainer = ({
  animated = false,
  height = '40px',
  radius = 'full',
  width = '40px',
}: SkeletonContainerProps &
  React.ComponentProps<typeof SkeletonTextWrapper>) => {
  return (
    <>
      <SkeletonContainerWrapper
        animated={animated}
        height={height}
        radius={radius}
        width={width}
      />
    </>
  );
};

type SkeletonTextProps = {
  animated?: boolean;
  lines?: number;
  size?: TextSize;
};

type SkeletonContainerProps = {
  animated?: boolean;
  height?: string;
  radius?: 'medium' | 'large' | 'xlarge' | 'full' | '175';
  width?: string;
};

export default SkeletonText;
